import { Dtos, formatToPercent, type NaicsCodeId } from "@inrev/common";
import { ContractSuretyType } from "@inrev/common/lib/esm/enums";
import { BondRequest, useUpdateBondRequest } from "@portal/api/bond-request/agent";
import { cn } from "@portal/components/lib/utils";
import { createFormResolver } from "@portal/components/multiform";
import { Button, Card, ClickableIcon } from "@portal/components/primitives";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useVersionConflictSync } from "@portal/hooks/useVersionConflictSync";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { z } from "zod";
import { formatEmpty } from "../../../utils";
import {
	getFormCompletion,
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	hasOwnPath,
	isEmpty,
} from "../../../utils/schema-fields";
import { AccountPartialSubmitConfirmationModal } from "../../shared/AccountPartialSubmitConfirmationModal";
import {
	ContractDamagesEditItem,
	ContractHazardsEditItem,
} from "../../shared/ContractSelectionEditItems";
import {
	VIEW_SECTION_STICKY_HEADER_CLASS,
	ViewSectionLayout,
} from "../../shared/layouts/ViewSectionLayout";
import {
	ActualBidDateInput,
	BidAmountInput,
	BidDateInput,
	BondAmountCurrencyPercentInput,
	BondAmountInput,
	type BondRequestRowProps,
	CompletionBondInput,
	ContractAmountInput,
	ContractDateInput,
	FinalBondFormTypeDropdown,
	LowestBidInput,
	ProjectDescriptionBlockquote,
	ProjectDescriptionInput,
	ProjectLocationInput,
	ProjectNameInput,
	ScopeOfWorkInput,
	SecondLowestBidInput,
	TimeToCompleteInput,
	TListingRequiredInput,
	WarrantyPeriodInput,
} from "../../shared/request/BondRequestDetailRows";
import {
	VIEW_SECTION_CARD_STACK_CLASS,
	ViewSectionCardEditItem,
} from "../../shared/ViewSectionCardEditItem";

type BondDetailsSectionProps = {
	request: BondRequest;
	accountNaicsCodes: NaicsCodeId[];
	/** Starts the section in edit mode. Useful for Storybook fixtures. */
	defaultEditing?: boolean;
};

type ContractUpdateForm = Dtos.SuretyQuote.Update.Request;
const ContractUpdateForm = Dtos.SuretyQuote.Update.Request;
const BidUpdateForm = Dtos.SuretyQuote.Update.Bid;
const FinalUpdateForm = Dtos.SuretyQuote.Update.Final;
const BidToFinalUpdateForm = Dtos.SuretyQuote.Update.BidToFinal;

type FormComponentProps = BondRequestRowProps<ContractUpdateForm>;

const EMPTY_NAICS_CODE_IDS: NaicsCodeId[] = [];

const BID_BOND_CURRENCY_PERCENT_NAMES = {
	referenceSourceName: "bidAmount",
	currencyName: "bondAmount",
	percentName: "bondAmountPercentOfBid",
	amountTypeName: "bidBondAmountType",
} as const;

export const BondDetailsSection = ({
	request,
	accountNaicsCodes,
	defaultEditing = false,
}: BondDetailsSectionProps) => {
	const [editing, setEditing] = useState(defaultEditing);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const requestRef = useRef(request);
	requestRef.current = request;
	const pendingSubmitDataRef = useRef<ContractUpdateForm | null>(null);
	const [partialSubmit, setPartialSubmit] = useState<{ percentComplete?: number } | null>(null);
	const status = request.status;
	const isEditable = status !== "pending" && status !== "declined" && status !== "closed";

	const scopeOfWorkIds = useMemo(
		() => request.project?.scopeOfWork?.map((row) => row.id) ?? EMPTY_NAICS_CODE_IDS,
		[request.project?.scopeOfWork],
	);

	const isBid = request.contractSuretyType === ContractSuretyType.bid;
	const isFinal = request.contractSuretyType === ContractSuretyType.final;
	const isB2F = request.contractSuretyType === ContractSuretyType.bid_to_final;

	const schema = match(request.contractSuretyType)
		.with(ContractSuretyType.bid, () => BidUpdateForm)
		.with(ContractSuretyType.final, () => FinalUpdateForm)
		.with(ContractSuretyType.bid_to_final, () => BidToFinalUpdateForm)
		.exhaustive();

	const toUpdateForm = (req: BondRequest) => {
		const parsed = ContractUpdateForm.schema.safeParse({
			...req,
			project: {
				...req.project,
				scopeOfWork: (req.project.scopeOfWork ?? []).map((row) => row.id),
				city: req.project.address?.city,
				state: req.project.address?.state,
			},
		});
		if (!parsed.success) throw new Error(z.prettifyError(parsed.error));
		return parsed.data;
	};

	const [initialValues] = useState(() => toUpdateForm(request));

	const resolverSchema = isMinimalUIEnabled
		? ContractUpdateForm.schema.refine(
				(data) => typeof data.project?.name === "string" && data.project.name.trim().length > 0,
				{ error: "Required", path: ["project", "name"] },
			)
		: ContractUpdateForm.schema.refine(
				(data) =>
					typeof data.project?.description === "string" &&
					data.project.description.trim().length > 0,
				{ error: "Required", path: ["project", "description"] },
			);

	const formMethods = useForm<ContractUpdateForm>({
		defaultValues: initialValues,
		mode: "onBlur",
		reValidateMode: "onBlur",
		resolver: createFormResolver<ContractUpdateForm>(resolverSchema),
	});
	const { formState, handleSubmit } = formMethods;

	const { updateBondRequest, updateBondRequestIsLoading } = useUpdateBondRequest(
		request.id,
		request.suretyAccountId,
		{
			onConflict: () => {
				formMethods.reset(toUpdateForm(requestRef.current));
				setEditing(false);
			},
		},
	);

	useVersionConflictSync(request.updatedAt, () => {
		if (!editing) formMethods.reset(toUpdateForm(request));
	});

	const submitUpdate = (data: ContractUpdateForm) => {
		updateBondRequest({
			data,
			onSuccess: () => {
				setEditing(false);
			},
		});
	};

	const onSubmit = (data: ContractUpdateForm) => {
		const emptyPaths = [
			...getPartialFieldPaths(schema, data),
			...getRequiredForApprovalFieldPaths(schema, data),
		].filter((path) => hasOwnPath(data, path) && isEmpty(getNestedValue(data, path)));

		if (emptyPaths.length > 0) {
			pendingSubmitDataRef.current = data;
			setPartialSubmit({ percentComplete: getFormCompletion(schema, data) });
			return;
		}
		submitUpdate(data);
	};

	const onConfirmedPartialSubmit = () => {
		const pendingData = pendingSubmitDataRef.current;
		pendingSubmitDataRef.current = null;
		setPartialSubmit(null);
		if (pendingData) submitUpdate(pendingData);
	};

	const handleCancel = useCallback(() => {
		formMethods.reset(toUpdateForm(request));
		setEditing(false);
	}, [formMethods, request]);

	// Escape backs out of edit mode from anywhere in the card, so a long form doesn't force a
	// scroll back to the action bar just to abandon a change.
	useEffect(() => {
		if (!editing || updateBondRequestIsLoading) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") handleCancel();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [editing, updateBondRequestIsLoading, handleCancel]);

	const componentProps: Omit<FormComponentProps, "name"> = {
		formMethods,
		editing,
		schema,
		partialRequiresSubmit: false,
		isMinimalUIEnabled,
	};

	return (
		<Card
			elevation="raised"
			className="rounded-xl"
			id="bond-details-section"
			data-testid="agent-bond-request-details-card"
		>
			<Card.Body className="p-6">
				<ViewSectionLayout
					header="Details"
					headerClassName={editing ? VIEW_SECTION_STICKY_HEADER_CLASS : undefined}
					button={
						isEditable ? (
							editing ? (
								<div className="flex space-x-[10px]">
									<Button
										variant="outline"
										color="secondary"
										size="sm"
										disabled={updateBondRequestIsLoading}
										onClick={handleCancel}
									>
										Cancel
									</Button>
									<Button
										variant="solid"
										color="primary"
										size="sm"
										disabled={!formState.isDirty}
										loading={updateBondRequestIsLoading}
										onClick={handleSubmit(onSubmit)}
									>
										Save
									</Button>
								</div>
							) : (
								<ClickableIcon
									type="pencil-square"
									size="mini"
									color="secondary"
									aria-label="Edit details"
									data-testid="agent-bond-request-details-edit"
									trackingId="bond-request-agent-details-edit"
									onClick={() => setEditing(true)}
								/>
							)
						) : undefined
					}
					partial={false}
					partialActive={false}
				>
					<FormProvider {...formMethods}>
						<div
							className={cn(
								"w-full h-full bg-surface-base rounded-md",
								VIEW_SECTION_CARD_STACK_CLASS,
							)}
						>
							{isMinimalUIEnabled && (
								<>
									<ProjectNameInput
										{...componentProps}
										projectName={request.project?.name}
										name="project.name"
									/>
									<ProjectDescriptionBlockquote
										{...componentProps}
										projectDescription={request.project?.description}
										testId="project-description"
										name="project.description"
									/>
								</>
							)}
							{isBid && (
								<>
									<BidDateInput {...componentProps} bidDate={request.bidDate} name="bidDate" />
									<BidAmountInput
										{...componentProps}
										bidAmount={request.bidAmount}
										name="bidAmount"
									/>
									<BondAmountCurrencyPercentInput
										{...componentProps}
										bondAmount={request.bondAmount}
										currencyPercentNames={BID_BOND_CURRENCY_PERCENT_NAMES}
										name="bondAmount"
									/>
									{request.bidBondAmountType === "percent" && !editing && (
										<ViewSectionCardEditItem
											label="Percent of Bid"
											name="bondAmountPercentOfBid"
											viewComponent={formatEmpty(formatToPercent(request.bondAmountPercentOfBid))}
											editComponent={formatEmpty(formatToPercent(request.bondAmountPercentOfBid))}
											{...componentProps}
										/>
									)}
									<FinalBondFormTypeDropdown
										{...componentProps}
										finalBondFormType={request.finalBondFormType}
										placeholder="Select final bond form type"
										name="finalBondFormType"
									/>
								</>
							)}
							{isB2F && (
								<>
									<LowestBidInput
										{...componentProps}
										actualBidAmount={request.actualBidAmount}
										name="actualBidAmount"
									/>
									<SecondLowestBidInput
										{...componentProps}
										secondBid={request.secondBid}
										name="secondBid"
									/>
									<ActualBidDateInput
										{...componentProps}
										actualBidDate={request.actualBidDate}
										name="actualBidDate"
									/>
									<ContractDateInput
										{...componentProps}
										contractDate={request.contractDate}
										name="contractDate"
									/>
								</>
							)}

							{(isFinal || isB2F) && (
								<>
									<BondAmountInput
										{...componentProps}
										bondAmount={request.bondAmount}
										name="bondAmount"
									/>
									<ContractAmountInput
										{...componentProps}
										contractAmount={request.contractAmount}
										name="contractAmount"
									/>
								</>
							)}
							<ScopeOfWorkInput
								{...componentProps}
								scopeOfWork={scopeOfWorkIds}
								accountNaicsCodes={accountNaicsCodes}
								name="project.scopeOfWork"
							/>

							{!isMinimalUIEnabled && (
								<ProjectDescriptionInput
									{...componentProps}
									projectDescription={request.project?.description}
									name="project.description"
								/>
							)}
							<ProjectLocationInput
								{...componentProps}
								city={request.project?.address?.city}
								state={request.project?.address?.state}
								name={["project.city", "project.state"]}
							/>
							<TimeToCompleteInput
								{...componentProps}
								monthsToComplete={request.project?.monthsToComplete}
								name="project.monthsToComplete"
							/>
							<WarrantyPeriodInput
								{...componentProps}
								contractWarrantyMonths={request.project?.contractWarrantyMonths}
								name="project.contractWarrantyMonths"
							/>

							<ContractDamagesEditItem
								{...componentProps}
								name="project.contractDamages"
								values={request.project?.contractDamages ?? undefined}
							/>
							<ContractHazardsEditItem
								{...componentProps}
								name="project.contractHazards"
								values={request.project?.contractHazards ?? undefined}
							/>
							<CompletionBondInput
								{...componentProps}
								completionBond={request.completionBond}
								name="completionBond"
							/>
							<TListingRequiredInput
								{...componentProps}
								tListingRequired={request.tListingRequired}
								name="tListingRequired"
							/>
						</div>
						{partialSubmit && (
							<AccountPartialSubmitConfirmationModal
								context="view"
								userContext="agent"
								percentComplete={partialSubmit.percentComplete}
								onClose={() => setPartialSubmit(null)}
								onSubmit={() => onConfirmedPartialSubmit()}
							/>
						)}
					</FormProvider>
				</ViewSectionLayout>
			</Card.Body>
		</Card>
	);
};
